import { luscinia } from "./luscinia";

// Draggable horizontal separator between two stacked panels (Visual Studio style).
// USAGE:
// - Give the parent element (a flex column) the class .luscinia-grid-splitter;
//   its height is fitted to the remaining viewport so both panels keep their own scrollbar
// - Place a div.luscinia-grid-splitter-handle between the two panels;
//   dragging it resizes its previous element sibling (the top panel)
// - Optional data-splitter-storage-key on the handle persists the top panel height in localStorage
// Styling lives in Core/scss/gridSplitter.scss

const HANDLE_CLASS = "luscinia-grid-splitter-handle";
// Grid chrome (action header, column headers, pagination footer) is ~230px per
// grid; keep at least a couple of rows visible on both sides of the handle
const MIN_TOP_HEIGHT = 200;
const MIN_BOTTOM_HEIGHT = 260;

luscinia.initGridSplitters = function (filter?: JQuery<HTMLElement>) {
    (filter ?? $(document.body)).find(`.${HANDLE_CLASS}`).each(function () {
        initHandle(this as HTMLElement);
    });
};

function initHandle(handle: HTMLElement) {
    if (handle.dataset.splitterInitialized) {
        return;
    }
    handle.dataset.splitterInitialized = "true";

    const container = handle.parentElement;
    const topPanel = handle.previousElementSibling as HTMLElement | null;
    if (!container || !topPanel) {
        return;
    }
    const storageKey = handle.dataset.splitterStorageKey;

    function fitContainer() {
        const top = container.getBoundingClientRect().top;
        const margin = parseFloat(getComputedStyle(container).marginBottom) || 0;
        container.style.height = Math.max(window.innerHeight - top - margin, 200) + "px";
    }

    function clampHeight(height: number): number {
        const max = container.clientHeight - MIN_BOTTOM_HEIGHT;
        return Math.min(Math.max(height, MIN_TOP_HEIGHT), Math.max(max, MIN_TOP_HEIGHT));
    }

    function setTopHeight(height: number) {
        topPanel.style.flexBasis = clampHeight(height) + "px";
    }

    fitContainer();
    window.addEventListener("resize", function () {
        fitContainer();
        setTopHeight(topPanel.getBoundingClientRect().height);
    });

    const saved = storageKey ? parseFloat(localStorage.getItem(storageKey) ?? "") : NaN;
    setTopHeight(!isNaN(saved) ? saved : container.clientHeight * 0.55);

    handle.addEventListener("pointerdown", function (e: PointerEvent) {
        e.preventDefault();
        handle.setPointerCapture(e.pointerId);
        handle.classList.add("dragging");
        document.body.style.userSelect = "none";
        const startY = e.clientY;
        const startHeight = topPanel.getBoundingClientRect().height;

        function onMove(ev: PointerEvent) {
            setTopHeight(startHeight + (ev.clientY - startY));
        }

        function onUp(ev: PointerEvent) {
            handle.removeEventListener("pointermove", onMove);
            handle.removeEventListener("pointerup", onUp);
            handle.releasePointerCapture(ev.pointerId);
            handle.classList.remove("dragging");
            document.body.style.userSelect = "";
            if (storageKey) {
                localStorage.setItem(storageKey, topPanel.getBoundingClientRect().height.toString());
            }
        }

        handle.addEventListener("pointermove", onMove);
        handle.addEventListener("pointerup", onUp);
    });
}

$(function () {
    luscinia.initGridSplitters();
});
